import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Plus, Tag, X } from 'lucide-react';
import { useRef, useState } from 'react';

interface TagInputProps {
  tags: Array<{ id: string; tag: string }>;
  suggestions?: Array<{ tag: string; color?: string }>;
  onAdd: (tag: string) => void;
  onRemove: (tagId: string) => void;
  maxTags?: number;
  disabled?: boolean;
}

export function TagInput({
  tags,
  suggestions = [],
  onAdd,
  onRemove,
  maxTags = 5,
  disabled = false,
}: TagInputProps) {
  const [inputValue, setInputValue] = useState('');
  const [showInput, setShowInput] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  const existingTagNames = new Set(tags.map((t) => t.tag));
  const filteredSuggestions = suggestions.filter(
    (s) =>
      !existingTagNames.has(s.tag) &&
      inputValue.length > 0 &&
      s.tag.includes(inputValue.toLowerCase())
  );

  const handleAdd = (tagName: string) => {
    const trimmed = tagName.trim().toLowerCase();
    if (
      !trimmed ||
      trimmed.length > 20 ||
      existingTagNames.has(trimmed) ||
      tags.length >= maxTags
    ) {
      return;
    }
    onAdd(trimmed);
    setInputValue('');
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleAdd(inputValue);
    }
    if (e.key === 'Escape') {
      setShowInput(false);
      setInputValue('');
    }
  };

  return (
    <div className="space-y-2">
      {/* Existing tags */}
      <div className="flex flex-wrap gap-1">
        {tags.map((t) => (
          <Badge key={t.id} variant="secondary" className="gap-1">
            {t.tag}
            {!disabled && (
              <button
                type="button"
                onClick={() => onRemove(t.id)}
                className="ml-1 rounded-full hover:bg-muted-foreground/20"
              >
                <X className="h-3 w-3" />
              </button>
            )}
          </Badge>
        ))}
        {tags.length === 0 && !showInput && (
          <span className="text-xs text-muted-foreground">No tags</span>
        )}
      </div>

      {/* Add tag */}
      {!disabled &&
        tags.length < maxTags &&
        (showInput ? (
          <div className="space-y-1">
            <div className="flex gap-1">
              <Input
                ref={inputRef}
                placeholder="Tag name..."
                value={inputValue}
                onChange={(e) => setInputValue(e.target.value)}
                onKeyDown={handleKeyDown}
                maxLength={20}
                className="h-7 text-xs"
                autoFocus
              />
              <Button
                size="sm"
                className="h-7 text-xs px-2"
                onClick={() => handleAdd(inputValue)}
                disabled={!inputValue.trim()}
              >
                Add
              </Button>
              <Button
                variant="ghost"
                size="sm"
                className="h-7 text-xs px-2"
                onClick={() => {
                  setShowInput(false);
                  setInputValue('');
                }}
              >
                <X className="h-3 w-3" />
              </Button>
            </div>
            {/* Suggestion list */}
            {filteredSuggestions.length > 0 && (
              <div className="border rounded-md bg-background max-h-24 overflow-y-auto">
                {filteredSuggestions.slice(0, 6).map((s) => (
                  <button
                    key={s.tag}
                    type="button"
                    className="flex w-full items-center gap-2 px-2 py-1 text-xs hover:bg-muted"
                    onClick={() => handleAdd(s.tag)}
                  >
                    <Tag className="h-3 w-3 text-muted-foreground" />
                    {s.tag}
                  </button>
                ))}
              </div>
            )}
            <p className="text-[10px] text-muted-foreground">
              {tags.length}/{maxTags} tags
            </p>
          </div>
        ) : (
          <Button
            variant="outline"
            size="sm"
            className="h-7 gap-1 text-xs"
            onClick={() => setShowInput(true)}
          >
            <Plus className="h-3 w-3" />
            Add Tag
          </Button>
        ))}
    </div>
  );
}
